import { Component, OnInit, ViewChildren, QueryList, ElementRef, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/api.service';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit, AfterViewInit {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  @ViewChild('dotsHost') dotsHost!: ElementRef<HTMLDivElement>;

  emailForm: FormGroup;
  otpForm: FormGroup;
  currentStep: 'email' | 'otp' = 'email';
  loading = false;
  error = '';
  currentEmail = '';
  otpDigits: string[] = ['', '', '', '', '', ''];

  // Dots grid config for left panel
  dotsWidth = 720;
  dotsHeight = 480; // visible area from bottom up
  dotRadius = 1.85;
  dotGap = 29.63; // px between dot centers
  dotCircles: Array<{ cx: number; cy: number }> = [];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  async ngOnInit() {
    // If already authenticated, redirect to stores
    if (AuthService.isAuthenticated()) {
      this.router.navigate(['/stores']);
      return;
    }

    // Check for URL parameters for automatic login
    this.route.queryParams.subscribe(async params => {
      const email = params['email'];
      const password = params['password'];

      if (email && password) {
        await this.attemptUrlLogin(email, password);
      }
    });

    // Initial build runs after view init when we know host width
  }

  ngAfterViewInit() {
    this.remeasureDots();
  }

  @HostListener('window:resize')
  onResize() {
    this.remeasureDots();
  }

  private remeasureDots() {
    if (!this.dotsHost) return;
    const w = this.dotsHost.nativeElement.clientWidth;
    if (w && Math.abs(w - this.dotsWidth) > 0.5) {
      this.dotsWidth = w;
      this.dotCircles = [];
      this.buildDotGrid();
    }
  }

  private buildDotGrid() {
    const cols = Math.floor(this.dotsWidth / this.dotGap);
    const rows = Math.floor(this.dotsHeight / this.dotGap);
    const bottomMargin = 0;

    for (let r = 0; r < rows; r++) {
      const y = this.dotsHeight - bottomMargin - r * this.dotGap - this.dotRadius;
      const remove = r; // remove one from each side per row
      const startCol = remove;
      const endCol = cols - remove - 1;
      for (let c = startCol; c <= endCol; c++) {
        const x = c * this.dotGap + this.dotRadius;
        this.dotCircles.push({ cx: x, cy: y });
      }
    }
  }

  private async attemptUrlLogin(email: string, password: string) {
    this.loading = true;
    this.error = '';

    try {
      const response = await firstValueFrom(this.authService.regularLoginUser(email, password));

      console.log(response);

      // Save auth data
      if (response) {
        this.authService.saveAuthData(response);
      }

      // Redirect to stores
      this.router.navigate(['/stores']);
    } catch (error: any) {
      this.error = error.error?.message || 'Login failed. Please try again.';
      // Pre-fill email form if login failed
      this.emailForm.patchValue({ email: email });
    } finally {
      this.loading = false;
    }
  }

  async onEmailSubmit() {
    if (this.emailForm.valid) {
      this.loading = true;
      this.error = '';
      this.currentEmail = this.emailForm.value.email;
      const domain = (window.location.hostname === 'localhost') ? 'stitch.agi.supply' : window.location.hostname;

      try {

        const portalResponse = await firstValueFrom(this.authService.getPortalId(domain));

        // Send OTP
        await firstValueFrom(this.authService.sendOtp(this.currentEmail));

        this.currentStep = 'otp';
        // Focus first OTP input after step change
        setTimeout(() => this.focusNextInput(0), 100);
      } catch (error: any) {
        // Check if user doesn't exist and needs to be registered
        console.log(error.error);
        if (error.error.error === 'User with this email not found') {
          try {
            const portalResponse = await firstValueFrom(this.authService.getPortalId(domain));

            // Register new user first
            await firstValueFrom(this.authService.registerUser(this.currentEmail, portalResponse.portal.service_name));

            // Then send OTP after successful registration
            await firstValueFrom(this.authService.sendOtp(this.currentEmail));

            this.currentStep = 'otp';
            // Focus first OTP input after step change
            setTimeout(() => this.focusNextInput(0), 100);
          } catch (registerError: any) {
            this.error = registerError.error?.message || 'Failed to create account. Please try again.';
          }
        } else {
          this.error = error.error?.message || 'An error occurred. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    }
  }

  async onOtpSubmit() {
    if (this.otpForm.valid) {
      this.loading = true;
      this.error = '';

      try {
        const response = await firstValueFrom(this.authService.verifyOtp(
          this.currentEmail,
          this.otpForm.value.otp
        ));

        // Save auth data
        if (response) {
          this.authService.saveAuthData(response);
        }

        // Redirect to stores
        this.router.navigate(['/stores']);
      } catch (error: any) {
        this.error = error.error?.message || 'Invalid OTP. Please try again.';
      } finally {
        this.loading = false;
      }
    }
  }

  async resendOtp() {
    this.loading = true;
    this.error = '';

    try {
      await firstValueFrom(this.authService.sendOtp(this.currentEmail));
      // Show success message or handle UI feedback
    } catch (error: any) {
      // Check if user doesn't exist and needs to be registered
      if (error.error?.message === 'User with this email not found') {
        try {
          const domain = (window.location.hostname === 'localhost') ? 'stage-stitch.agi.supply' : window.location.hostname;

          const portalResponse = await firstValueFrom(this.authService.getPortalId(domain));

          // Register new user first
          await firstValueFrom(this.authService.registerUser(this.currentEmail, portalResponse.portal.service_name));

          // Then send OTP after successful registration
          await firstValueFrom(this.authService.sendOtp(this.currentEmail));
        } catch (registerError: any) {
          this.error = registerError.error?.message || 'Failed to create account. Please try again.';
        }
      } else {
        this.error = error.error?.message || 'Failed to resend OTP.';
      }
    } finally {
      this.loading = false;
    }
  }

  goBackToEmail() {
    this.currentStep = 'email';
    this.otpForm.reset();
    this.clearOtpDigits();
    this.error = '';
  }

  onOtpDigitInput(event: any, index: number) {
    const value = event.target.value;

    // Only allow digits
    if (!/^\d*$/.test(value)) {
      event.target.value = this.otpDigits[index];
      return;
    }

    // Update the digit
    this.otpDigits[index] = value;

    // Update the form control
    this.updateOtpFormControl();

    // Move to next input if digit was entered
    if (value && index < 5) {
      this.focusNextInput(index + 1);
    }

    // Check for auto-submit if this was the last digit
    if (value && index === 5) {
      this.checkAndAutoSubmit();
    }
  }

  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const target = event.target as HTMLInputElement;

    // Handle backspace
    if (event.key === 'Backspace') {
      if (this.otpDigits[index]) {
        // If current input has a digit, clear it and move to previous input
        this.otpDigits[index] = '';
        target.value = '';
        this.updateOtpFormControl();
        if (index > 0) {
          this.focusNextInput(index - 1);
        }
      } else if (index > 0) {
        // If current input is empty, move to previous input and clear it
        this.otpDigits[index - 1] = '';
        this.updateOtpFormControl();
        this.focusNextInput(index - 1);
      }
      event.preventDefault();
    }

    // Handle arrow keys
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusNextInput(index - 1);
    }

    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusNextInput(index + 1);
    }
  }

  onOtpPaste(event: ClipboardEvent, index: number) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);

    // Fill the digits starting from the current index
    for (let i = 0; i < digits.length && (index + i) < 6; i++) {
      this.otpDigits[index + i] = digits[i];
    }

    // Update form and focus
    this.updateOtpFormControl();
    const nextIndex = Math.min(index + digits.length, 5);
    this.focusNextInput(nextIndex);

    // Check for auto-submit after paste
    this.checkAndAutoSubmit();
  }

  private focusNextInput(index: number) {
    setTimeout(() => {
      const inputs = this.otpInputs.toArray();
      if (inputs[index]) {
        const inputElement = inputs[index].nativeElement;
        inputElement.focus();
        inputElement.value = this.otpDigits[index];
        inputElement.select();
      }
    }, 10);
  }

  private updateOtpFormControl() {
    const otpValue = this.otpDigits.join('');
    this.otpForm.patchValue({ otp: otpValue });
  }

  private clearOtpDigits() {
    this.otpDigits = ['', '', '', '', '', ''];
    this.updateOtpFormControl();
  }

  private checkAndAutoSubmit() {
    const isComplete = this.otpDigits.every(digit => digit !== '');
    if (isComplete) {
      // Small delay to ensure UI updates, then auto-submit
      setTimeout(() => {
        this.onOtpSubmit();
      }, 250);
    }
  }
} 